import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostBinding,
  inject,
  computed,
  effect,
  input,
  output,
  signal,
  contentChildren,
  untracked
} from '@angular/core';
import { IconComponent } from '@shared/components/ui/icon';

export type TabsVariant = 'underline' | 'pills' | 'enclosed';
export type TabsSize = 'sm' | 'md' | 'lg';

export interface Tab {
  id: string;
  label: string;
  disabled?: boolean;
  icon?: string;
  badge?: string | number;
}

let tabIdCounter = 0;

/**
 * GAP-FE-021B - migrated from decorator @Input/@Output to signal input()/output().
 */
@Component({
  selector: 'ui-tab',
  standalone: true,
  imports: [],
  templateUrl: './tab.component.html',
  styleUrl: './tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabComponent {
  // Inputs via aliased signal inputs. Internal writable signals back the
  // public read API so that:
  //   - `header` (legacy alias) can populate `label`/`id`,
  //   - `id` keeps its lazy auto-generated fallback,
  //   - `disabled`/`icon`/`badge`/`label` can still be read as `component.X()`.
  // NOTE: protected (not private) because strictInputAccessModifiers blocks private inputs.
  readonly idInput = input<string | undefined>(undefined, { alias: 'id' });
  readonly labelInput = input<string>('', { alias: 'label' });
  readonly headerInput = input<string | undefined>(undefined, { alias: 'header' });
  readonly disabledInput = input<boolean>(false, { alias: 'disabled' });
  readonly iconInput = input<string | undefined>(undefined, { alias: 'icon' });
  readonly badgeInput = input<string | number | undefined>(undefined, { alias: 'badge' });
  /** Optional panel ownership; callers may retain the header while omitting inactive content. */
  readonly renderPanel = input(true);

  /** Stable resolved id (caller-provided OR generated once). */
  private readonly _id = signal<string | undefined>(undefined);
  private readonly _label = signal<string>('');
  private readonly _disabled = signal<boolean>(false);
  private readonly _icon = signal<string | undefined>(undefined);
  private readonly _badge = signal<string | number | undefined>(undefined);

  /** Whether this tab is currently active (controlled by parent TabsComponent). */
  readonly active = signal<boolean>(false);

  /** Public signal accessors - external reads use `component.X()`. */
  readonly id = computed<string>(() => {
    const provided = this._id();
    if (provided) {
      return provided;
    }
    // Generate a stable ID once and cache it
    if (!this._generatedId) {
      this._generatedId = `tab-${tabIdCounter++}`;
    }
    return this._generatedId;
  });
  readonly label = computed<string>(() => this._label());
  readonly disabled = computed<boolean>(() => this._disabled());
  readonly icon = computed<string | undefined>(() => this._icon());
  readonly badge = computed<string | number | undefined>(() => this._badge());

  /** Header is the legacy TabView alias for label. */
  readonly header = computed<string | undefined>(() => this.headerInput());

  /** Cached auto-generated id (matches pre-migration behaviour). */
  private _generatedId?: string;

  /** Apply .active class to host element when tab is active. */
  @HostBinding('class.active')
  get isActive(): boolean {
    return this.active();
  }

  constructor() {
    // Sync caller-provided id into _id. `header` can also seed _id when no
    // explicit id was supplied.
    effect(() => {
      const v = this.idInput();
      if (v !== undefined) {
        this._id.set(v);
      }
    });

    effect(() => {
      this._label.set(this.labelInput());
    });

    effect(() => {
      const h = this.headerInput();
      if (h !== undefined) {
        this._label.set(h);
        // legacy behaviour: derive id from header when no explicit id given
        if (this._id() === undefined) {
          this._id.set(h.toLowerCase().replace(/\s+/g, '-'));
        }
      }
    });

    effect(() => {
      this._disabled.set(this.disabledInput());
    });

    effect(() => {
      this._icon.set(this.iconInput());
    });

    effect(() => {
      this._badge.set(this.badgeInput());
    });
  }
}

/**
 * GAP-FE-021B - migrated from decorator @Input/@Output to signal input()/output().
 */
@Component({
  selector: 'ui-tabs',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabsComponent {
  private cdr = inject(ChangeDetectorRef);

  // Pure signal inputs
  readonly variant = input<TabsVariant>('underline');
  readonly size = input<TabsSize>('md');
  readonly fullWidth = input<boolean>(false);
  readonly scrollable = input<boolean>(false);
  readonly wrapOnMobile = input<boolean>(false);
  readonly ariaLabel = input<string | undefined>(undefined);

  // Aliased inputs for internally-mutated state (rule 7).
  // NOTE: protected (not private) because strictInputAccessModifiers blocks private inputs.
  readonly activeTabInput = input<string | undefined>(undefined, { alias: 'activeTab' });
  readonly activeIndexInput = input<number>(0, { alias: 'activeIndex' });

  /** Internal writable signals - selectTab() / activeIndex effect mutate these. */
  readonly activeTab = signal<string | undefined>(undefined);
  readonly activeIndex = signal<number>(0);

  // Outputs
  readonly tabChange = output<string>();
  readonly activeTabChange = output<number>();
  /** Two-way binding support for activeIndex: [(activeIndex)]="myIndex" */
  readonly activeIndexChange = output<number>();

  /** ContentChildren via signal API; returns readonly array. */
  readonly tabComponents = contentChildren(TabComponent);

  readonly tabs = signal<Tab[]>([]);

  /** Screen reader announcement for tab changes. */
  tabAnnouncement = '';

  constructor() {
    // Propagate external activeTab input changes into internal signal.
    effect(() => {
      const v = this.activeTabInput();
      if (v !== undefined) {
        this.activeTab.set(v);
        untracked(() => {
          const index = this.tabs().findIndex(tab => tab.id === v);
          if (index >= 0) {
            this.activeIndex.set(index);
          }
          this.updateTabActiveStates();
        });
      }
    });

    // Propagate external activeIndex input changes into internal signal +
    // sync the corresponding activeTab id (legacy setter semantics).
    effect(() => {
      const index = this.activeIndexInput();
      this.activeIndex.set(index);
      if (this.activeTabInput() === undefined) {
        untracked(() => {
          const list = this.tabs();
          if (list.length > 0 && index >= 0 && index < list.length) {
            this.activeTab.set(list[index].id);
            this.updateTabActiveStates();
          }
        });
      }
    });

    // Mirror previous ngAfterContentInit + tabComponents.changes subscription.
    // This effect should react ONLY to the projected tab children changing. The
    // sync logic both reads and writes activeTab/activeIndex and calls
    // updateTabActiveStates() (which reads activeTab) — running that in the
    // tracked scope would make selectTab()'s activeTab write re-trigger this
    // effect, which then re-reads the controlled activeTabInput and reverts the
    // selection. Wrap the sync in untracked() so it can't self-retrigger.
    effect(() => {
      // Keep the tab mapping TRACKED so changes to a child tab's id/label/
      // disabled/icon/badge rebuild the tabs() list.
      const mapped: Tab[] = this.tabComponents().map(tab => ({
        id: tab.id(),
        label: tab.label(),
        disabled: tab.disabled(),
        icon: tab.icon(),
        badge: tab.badge()
      }));
      this.tabs.set(mapped);

      // Run the active-state sync UNTRACKED: it both reads and writes
      // activeTab/activeIndex and calls updateTabActiveStates() (which reads
      // activeTab). Tracking those would make selectTab()'s activeTab write
      // re-trigger this effect, which then re-reads the controlled
      // activeTabInput and reverts the selection.
      untracked(() => {
        const controlledActiveTab = this.activeTabInput();
        if (controlledActiveTab !== undefined) {
          this.activeTab.set(controlledActiveTab);
          const activeIndex = mapped.findIndex(tab => tab.id === controlledActiveTab);
          if (activeIndex >= 0) {
            this.activeIndex.set(activeIndex);
          }
        } else if (this.activeIndex() >= 0 && this.activeIndex() < mapped.length) {
          this.activeTab.set(mapped[this.activeIndex()].id);
        } else if (!this.activeTab() && mapped.length > 0) {
          this.activeTab.set(mapped[0].id);
          this.activeIndex.set(0);
        }

        this.updateTabActiveStates();
      });
    });
  }

  /** Update the active state on all TabComponent instances. */
  private updateTabActiveStates(): void {
    const active = this.activeTab();
    this.tabComponents().forEach(tabComp => {
      tabComp.active.set(tabComp.id() === active);
    });
    // Ensure change detection runs for OnPush strategy
    this.cdr.detectChanges();
  }

  /** Tab list container classes */
  readonly tabListClasses = computed<string>(() => {
    const baseClasses = ['flex'];
    const variant = this.variant();

    if (variant === 'underline') {
      baseClasses.push('border-b', 'border-bb-border');
    } else if (variant === 'enclosed') {
      baseClasses.push('bg-bb-bg-tertiary', 'rounded-t-lg', 'p-1', 'gap-1');
    } else if (variant === 'pills') {
      baseClasses.push('bg-bb-bg-tertiary', 'rounded-lg', 'p-1', 'gap-1');
    }

    if (this.fullWidth()) {
      baseClasses.push('w-full');
    }

    // Responsive behavior
    if (this.scrollable()) {
      baseClasses.push('tabs-list-scrollable', 'overflow-x-auto', 'scrollbar-thin');
    } else if (this.wrapOnMobile()) {
      baseClasses.push('tabs-list-wrap', 'flex-wrap', 'sm:flex-nowrap');
    }

    return baseClasses.join(' ');
  });

  /** Individual tab button classes */
  getTabClasses(tab: Tab): string {
    const isActive = this.activeTab() === tab.id;
    const variant = this.variant();
    const size = this.size();
    const baseClasses = [
      'flex',
      'items-center',
      'justify-center',
      'gap-2',
      'font-medium',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'focus-visible:ring-2',
      'focus-visible:ring-bb-accent-blue',
      'focus-visible:ring-offset-2',
      'focus-visible:ring-offset-bb-bg-primary',
      // Touch-friendly minimum size
      'min-h-[44px]',
      'sm:min-h-0'
    ];

    // Size classes
    const sizeClasses: Record<TabsSize, string[]> = {
      sm: ['px-3', 'py-1.5', 'text-xs'],
      md: ['px-4', 'py-2', 'text-sm'],
      lg: ['px-6', 'py-3', 'text-base']
    };

    // Variant-specific classes with explicit dark mode support
    if (variant === 'underline') {
      baseClasses.push('-mb-px', 'border-b-2');
      if (isActive) {
        baseClasses.push(
          'border-bb-accent-blue',
          'text-bb-text-primary'
        );
      } else {
        baseClasses.push(
          'border-transparent',
          'text-bb-text-secondary',
          'hover:text-bb-text-primary',
          'hover:border-bb-border'
        );
      }
    } else if (variant === 'pills') {
      baseClasses.push('rounded-md');
      if (isActive) {
        baseClasses.push(
          'bg-bb-accent-blue',
          'text-white'
        );
      } else {
        baseClasses.push(
          'text-bb-text-secondary',
          'hover:text-bb-text-primary',
          'hover:bg-bb-bg-secondary'
        );
      }
    } else if (variant === 'enclosed') {
      baseClasses.push('rounded-md');
      if (isActive) {
        baseClasses.push(
          'bg-bb-bg-secondary',
          'text-bb-text-primary',
          'shadow-sm'
        );
      } else {
        baseClasses.push(
          'text-bb-text-secondary',
          'hover:text-bb-text-primary'
        );
      }
    }

    // Disabled state
    if (tab.disabled) {
      baseClasses.push('opacity-50', 'cursor-not-allowed', 'pointer-events-none');
    } else {
      baseClasses.push('cursor-pointer');
    }

    // Full width
    if (this.fullWidth()) {
      baseClasses.push('flex-1');
    }

    // Responsive: smaller padding on mobile when wrapping
    if (this.wrapOnMobile()) {
      baseClasses.push('max-sm:px-2', 'max-sm:text-xs');
    }

    return [...baseClasses, ...sizeClasses[size]].join(' ');
  }

  /** Badge classes */
  readonly badgeClasses = computed<string>(() => {
    const size = this.size();
    const sizeClasses: Record<TabsSize, string[]> = {
      sm: ['text-2xs', 'px-1', 'py-0.5', 'min-w-[16px]'],
      md: ['text-xs', 'px-1.5', 'py-0.5', 'min-w-[18px]'],
      lg: ['text-xs', 'px-2', 'py-1', 'min-w-[20px]']
    };

    return [
      'inline-flex',
      'items-center',
      'justify-center',
      'rounded-full',
      'bg-bb-accent-blue',
      'text-white',
      'font-semibold',
      ...sizeClasses[size]
    ].join(' ');
  });

  /** Select a tab */
  selectTab(tab: Tab): void {
    if (tab.disabled) {return;}
    this.activeTab.set(tab.id);
    // Update internal index for two-way binding
    const newIndex = this.tabs().findIndex(t => t.id === tab.id);
    this.activeIndex.set(newIndex);
    // Update active class on tab components
    this.updateTabActiveStates();
    // Announce tab change to screen readers
    this.tabAnnouncement = `${tab.label} tab selected`;
    this.tabChange.emit(tab.id);
    // Emit index for [(activeIndex)] two-way binding compatibility
    this.activeIndexChange.emit(newIndex);
    this.activeTabChange.emit(newIndex);
  }

  /** Handle keyboard navigation */
  onKeyDown(event: KeyboardEvent, currentIndex: number): void {
    const tabsList = this.tabs();
    const enabledTabs = tabsList.filter(t => !t.disabled);
    const currentEnabledIndex = enabledTabs.findIndex(t => t.id === tabsList[currentIndex].id);

    let newIndex = currentEnabledIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledTabs.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = currentEnabledIndex < enabledTabs.length - 1 ? currentEnabledIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = enabledTabs.length - 1;
        break;
      default:
        return;
    }

    this.selectTab(enabledTabs[newIndex]);
  }

  /** Check if tab is active */
  isActiveTab(tabId: string): boolean {
    return this.activeTab() === tabId;
  }
}
